
export type EccLevel = 'L' | 'M' | 'Q' | 'H';

export interface QrMatrix {
  size: number;
  modules: boolean[];
}

const MIN_VERSION = 1;
const MAX_VERSION = 10;

const MODE_BYTE = 0b0100;

const PAD_CODEWORDS = [0xec, 0x11] as const;

const ECC_LEVELS: readonly EccLevel[] = ['L', 'M', 'Q', 'H'];

const ECC_FORMAT_BITS: Readonly<Record<EccLevel, number>> = { L: 1, M: 0, Q: 3, H: 2 };

const ECC_CODEWORDS_PER_BLOCK: Readonly<Record<EccLevel, readonly number[]>> = {
  L: [0, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18],
  M: [0, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26],
  Q: [0, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24],
  H: [0, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28],
};

const ECC_BLOCK_COUNT: Readonly<Record<EccLevel, readonly number[]>> = {
  L: [0, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4],
  M: [0, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5],
  Q: [0, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8],
  H: [0, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8],
};

const PENALTY_N1 = 3;
const PENALTY_N2 = 3;
const PENALTY_N3 = 40;
const PENALTY_N4 = 10;


export function encodeQr(text: string, minEcc: EccLevel = 'M'): QrMatrix {
  const bytes = utf8Encode(text);
  const version = chooseVersion(bytes.length, minEcc);
  const codewords = buildCodewords(bytes, version, minEcc);

  const cv = newCanvas(version);
  drawFunctionPatterns(cv, version);
  placeCodewords(cv, codewords);

  let bestMask = 0;
  let bestPenalty = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    applyMask(cv, mask);
    drawFormatBits(cv, minEcc, mask);
    const penalty = maskPenalty(cv);
    if (penalty < bestPenalty) {
      bestPenalty = penalty;
      bestMask = mask;
    }
    applyMask(cv, mask);
  }
  applyMask(cv, bestMask);
  drawFormatBits(cv, minEcc, bestMask);

  return { size: cv.size, modules: cv.modules };
}

export function qrToSvgPath(m: QrMatrix): string {
  const parts: string[] = [];
  for (let row = 0; row < m.size; row++) {
    for (let col = 0; col < m.size; col++) {
      if (m.modules[row * m.size + col]) parts.push(`M${col} ${row}h1v1h-1z`);
    }
  }
  return parts.join('');
}


function totalCodewords(version: number): number {
  let modules = (16 * version + 128) * version + 64;
  if (version >= 2) {
    const numAlign = Math.floor(version / 7) + 2;
    modules -= (25 * numAlign - 10) * numAlign - 55;
    if (version >= 7) modules -= 36;
  }
  return Math.floor(modules / 8);
}

function dataCodewords(version: number, ecc: EccLevel): number {
  return (
    totalCodewords(version) -
    ECC_CODEWORDS_PER_BLOCK[ecc][version]! * ECC_BLOCK_COUNT[ecc][version]!
  );
}

function charCountBits(version: number): number {
  return version <= 9 ? 8 : 16;
}

function maxPayloadBytes(version: number, ecc: EccLevel): number {
  const usable = dataCodewords(version, ecc) * 8 - 4 - charCountBits(version);
  return Math.max(0, Math.floor(usable / 8));
}

function chooseVersion(byteLength: number, ecc: EccLevel): number {
  for (let version = MIN_VERSION; version <= MAX_VERSION; version++) {
    if (byteLength <= maxPayloadBytes(version, ecc)) return version;
  }
  const limit = maxPayloadBytes(MAX_VERSION, ecc);
  const roomier = ECC_LEVELS.find((l) => maxPayloadBytes(MAX_VERSION, l) > byteLength);
  const hint =
    roomier === undefined
      ? ''
      : ` Try level ${roomier} (up to ${maxPayloadBytes(MAX_VERSION, roomier)} bytes).`;
  throw new Error(
    `encodeQr: ${byteLength} bytes is too long for a version ${MAX_VERSION} symbol at ` +
      `error-correction level ${ecc} (maximum ${limit} bytes).${hint}`,
  );
}


function utf8Encode(text: string): number[] {
  const out: number[] = [];
  for (const ch of text) {
    let cp = ch.codePointAt(0)!;
    if (cp >= 0xd800 && cp <= 0xdfff) cp = 0xfffd;
    if (cp < 0x80) {
      out.push(cp);
    } else if (cp < 0x800) {
      out.push(0xc0 | (cp >> 6), 0x80 | (cp & 0x3f));
    } else if (cp < 0x10000) {
      out.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
    } else {
      out.push(
        0xf0 | (cp >> 18),
        0x80 | ((cp >> 12) & 0x3f),
        0x80 | ((cp >> 6) & 0x3f),
        0x80 | (cp & 0x3f),
      );
    }
  }
  return out;
}


const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);
{
  let x = 1;
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x;
    GF_LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255]!;
}

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return GF_EXP[GF_LOG[a]! + GF_LOG[b]!]!;
}

function rsGeneratorPoly(degree: number): Uint8Array {
  const poly = new Uint8Array(degree);
  poly[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < degree; j++) {
      const scaled = gfMul(poly[j]!, root);
      poly[j] = j + 1 < degree ? scaled ^ poly[j + 1]! : scaled;
    }
    root = gfMul(root, 2);
  }
  return poly;
}

function rsRemainder(data: readonly number[], generator: Uint8Array): number[] {
  const result = new Uint8Array(generator.length);
  for (const byte of data) {
    const factor = byte ^ result[0]!;
    result.copyWithin(0, 1);
    result[result.length - 1] = 0;
    for (let i = 0; i < result.length; i++) result[i] = result[i]! ^ gfMul(generator[i]!, factor);
  }
  return Array.from(result);
}


function appendBits(bits: number[], value: number, length: number): void {
  for (let i = length - 1; i >= 0; i--) bits.push((value >>> i) & 1);
}

function buildCodewords(bytes: readonly number[], version: number, ecc: EccLevel): number[] {
  const capacity = dataCodewords(version, ecc);

  const bits: number[] = [];
  appendBits(bits, MODE_BYTE, 4);
  appendBits(bits, bytes.length, charCountBits(version));
  for (const b of bytes) appendBits(bits, b, 8);

  appendBits(bits, 0, Math.min(4, capacity * 8 - bits.length));
  appendBits(bits, 0, (8 - (bits.length % 8)) % 8);
  for (let i = 0; bits.length < capacity * 8; i++) {
    appendBits(bits, PAD_CODEWORDS[i % 2]!, 8);
  }

  const data: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j++) byte = (byte << 1) | bits[i + j]!;
    data.push(byte);
  }

  return interleave(data, version, ecc);
}

function interleave(data: readonly number[], version: number, ecc: EccLevel): number[] {
  const numBlocks = ECC_BLOCK_COUNT[ecc][version]!;
  const eccLen = ECC_CODEWORDS_PER_BLOCK[ecc][version]!;
  const generator = rsGeneratorPoly(eccLen);

  const shortLen = Math.floor(data.length / numBlocks);
  const numShort = numBlocks - (data.length % numBlocks);

  const dataBlocks: number[][] = [];
  const eccBlocks: number[][] = [];
  let offset = 0;
  for (let b = 0; b < numBlocks; b++) {
    const len = shortLen + (b < numShort ? 0 : 1);
    const block = data.slice(offset, offset + len);
    offset += len;
    dataBlocks.push(block);
    eccBlocks.push(rsRemainder(block, generator));
  }

  const out: number[] = [];
  const longLen = shortLen + (numShort < numBlocks ? 1 : 0);
  for (let i = 0; i < longLen; i++) {
    for (const block of dataBlocks) {
      if (i < block.length) out.push(block[i]!);
    }
  }
  for (let i = 0; i < eccLen; i++) {
    for (const block of eccBlocks) out.push(block[i]!);
  }
  return out;
}


interface Canvas {
  size: number;
  modules: boolean[];
  reserved: boolean[];
}

function newCanvas(version: number): Canvas {
  const size = version * 4 + 17;
  return {
    size,
    modules: new Array<boolean>(size * size).fill(false),
    reserved: new Array<boolean>(size * size).fill(false),
  };
}

function setFunction(cv: Canvas, row: number, col: number, dark: boolean): void {
  if (row < 0 || row >= cv.size || col < 0 || col >= cv.size) return;
  const i = row * cv.size + col;
  cv.modules[i] = dark;
  cv.reserved[i] = true;
}

function alignmentPositions(version: number): number[] {
  if (version === 1) return [];
  const count = Math.floor(version / 7) + 2;
  const size = version * 4 + 17;
  const step = Math.ceil((version * 4 + 4) / (count * 2 - 2)) * 2;
  const result = [6];
  for (let pos = size - 7; result.length < count; pos -= step) result.splice(1, 0, pos);
  return result;
}

function drawFunctionPatterns(cv: Canvas, version: number): void {
  const size = cv.size;

  for (let i = 0; i < size; i++) {
    setFunction(cv, 6, i, i % 2 === 0);
    setFunction(cv, i, 6, i % 2 === 0);
  }

  for (const [row, col] of [
    [3, 3],
    [3, size - 4],
    [size - 4, 3],
  ] as const) {
    for (let dr = -4; dr <= 4; dr++) {
      for (let dc = -4; dc <= 4; dc++) {
        const dist = Math.max(Math.abs(dr), Math.abs(dc));
        setFunction(cv, row + dr, col + dc, dist !== 2 && dist !== 4);
      }
    }
  }

  const positions = alignmentPositions(version);
  const last = positions.length - 1;
  for (let i = 0; i <= last; i++) {
    for (let j = 0; j <= last; j++) {
      if ((i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0)) continue;
      for (let dr = -2; dr <= 2; dr++) {
        for (let dc = -2; dc <= 2; dc++) {
          const dark = Math.max(Math.abs(dr), Math.abs(dc)) !== 1;
          setFunction(cv, positions[i]! + dr, positions[j]! + dc, dark);
        }
      }
    }
  }

  drawFormatBits(cv, 'M', 0);
  drawVersionBits(cv, version);
}

function drawFormatBits(cv: Canvas, ecc: EccLevel, mask: number): void {
  const size = cv.size;
  const data = (ECC_FORMAT_BITS[ecc] << 3) | mask;

  let rem = data;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  const bits = ((data << 10) | rem) ^ 0x5412;

  const bit = (i: number): boolean => ((bits >>> i) & 1) !== 0;

  for (let i = 0; i <= 5; i++) setFunction(cv, i, 8, bit(i));
  setFunction(cv, 7, 8, bit(6));
  setFunction(cv, 8, 8, bit(7));
  setFunction(cv, 8, 7, bit(8));
  for (let i = 9; i < 15; i++) setFunction(cv, 8, 14 - i, bit(i));

  for (let i = 0; i < 8; i++) setFunction(cv, 8, size - 1 - i, bit(i));
  for (let i = 8; i < 15; i++) setFunction(cv, size - 15 + i, 8, bit(i));

  setFunction(cv, size - 8, 8, true);
}

function drawVersionBits(cv: Canvas, version: number): void {
  if (version < 7) return;
  let rem = version;
  for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
  const bits = (version << 12) | rem;

  for (let i = 0; i < 18; i++) {
    const dark = ((bits >>> i) & 1) !== 0;
    const a = cv.size - 11 + (i % 3);
    const b = Math.floor(i / 3);
    setFunction(cv, b, a, dark);
    setFunction(cv, a, b, dark);
  }
}

function placeCodewords(cv: Canvas, codewords: readonly number[]): void {
  const size = cv.size;
  let bitIndex = 0;
  const totalBits = codewords.length * 8;

  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vert = 0; vert < size; vert++) {
      for (let j = 0; j < 2; j++) {
        const col = right - j;
        const upward = ((right + 1) & 2) === 0;
        const row = upward ? size - 1 - vert : vert;
        const i = row * size + col;
        if (!cv.reserved[i] && bitIndex < totalBits) {
          const byte = codewords[bitIndex >>> 3]!;
          cv.modules[i] = ((byte >>> (7 - (bitIndex & 7))) & 1) !== 0;
          bitIndex++;
        }
      }
    }
  }
}

function maskCondition(mask: number, row: number, col: number): boolean {
  switch (mask) {
    case 0:
      return (row + col) % 2 === 0;
    case 1:
      return row % 2 === 0;
    case 2:
      return col % 3 === 0;
    case 3:
      return (row + col) % 3 === 0;
    case 4:
      return (Math.floor(row / 2) + Math.floor(col / 3)) % 2 === 0;
    case 5:
      return ((row * col) % 2) + ((row * col) % 3) === 0;
    case 6:
      return (((row * col) % 2) + ((row * col) % 3)) % 2 === 0;
    case 7:
      return (((row + col) % 2) + ((row * col) % 3)) % 2 === 0;
    default:
      throw new Error(`encodeQr: mask ${mask} is out of range 0-7`);
  }
}

function applyMask(cv: Canvas, mask: number): void {
  for (let row = 0; row < cv.size; row++) {
    for (let col = 0; col < cv.size; col++) {
      const i = row * cv.size + col;
      if (!cv.reserved[i] && maskCondition(mask, row, col)) cv.modules[i] = !cv.modules[i];
    }
  }
}


const FINDER_LIKE = [true, false, true, true, true, false, true, false, false, false, false];

function maskPenalty(cv: Canvas): number {
  const size = cv.size;
  const at = (row: number, col: number): boolean => cv.modules[row * size + col]!;
  let penalty = 0;

  const runPenalty = (get: (i: number) => boolean): number => {
    let score = 0;
    let run = 1;
    for (let i = 1; i < size; i++) {
      if (get(i) === get(i - 1)) {
        run++;
        if (run === 5) score += PENALTY_N1;
        else if (run > 5) score += 1;
      } else {
        run = 1;
      }
    }
    return score;
  };
  for (let i = 0; i < size; i++) {
    penalty += runPenalty((j) => at(i, j));
    penalty += runPenalty((j) => at(j, i));
  }

  for (let row = 0; row + 1 < size; row++) {
    for (let col = 0; col + 1 < size; col++) {
      const c = at(row, col);
      if (c === at(row, col + 1) && c === at(row + 1, col) && c === at(row + 1, col + 1)) {
        penalty += PENALTY_N2;
      }
    }
  }

  const patternLength = FINDER_LIKE.length;
  for (let line = 0; line < size; line++) {
    for (let start = 0; start + patternLength <= size; start++) {
      let rowFwd = true;
      let rowRev = true;
      let colFwd = true;
      let colRev = true;
      for (let k = 0; k < patternLength && (rowFwd || rowRev || colFwd || colRev); k++) {
        const rowBit = at(line, start + k);
        const colBit = at(start + k, line);
        const want = FINDER_LIKE[k]!;
        const wantReversed = FINDER_LIKE[patternLength - 1 - k]!;
        if (rowBit !== want) rowFwd = false;
        if (rowBit !== wantReversed) rowRev = false;
        if (colBit !== want) colFwd = false;
        if (colBit !== wantReversed) colRev = false;
      }
      if (rowFwd) penalty += PENALTY_N3;
      if (rowRev) penalty += PENALTY_N3;
      if (colFwd) penalty += PENALTY_N3;
      if (colRev) penalty += PENALTY_N3;
    }
  }

  let dark = 0;
  for (const m of cv.modules) if (m) dark++;
  const total = size * size;
  penalty += (Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1) * PENALTY_N4;

  return penalty;
}
